import { ArrowUpDown, ChevronDown, ChevronUp, Trash2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ConferimentoListItem } from '../../api/conferimenti';
import { fmtKg } from '../../lib/saldo';

function formatDateIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_CLS: Record<string, string> = {
  programmato: 'bg-blue-100 text-blue-700',
  in_ritardo: 'bg-red-100 text-red-700',
  in_transito: 'bg-cyan-100 text-cyan-700',
  attesa_compliance: 'bg-amber-100 text-amber-700',
  in_stoccaggio: 'bg-green-100 text-green-700',
  respinto: 'bg-purple-100 text-purple-700',
};

type SortKey = 'data_prevista' | 'numero' | 'status';

interface Props {
  items: ConferimentoListItem[];
  total: number;
  limit: number;
  offset: number;
  onPageChange: (offset: number) => void;
  sortBy: SortKey;
  sortDir: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
  onDelete?: (id: string) => void;
}

function SortIcon({ col, sortBy, sortDir }: { col: string; sortBy: SortKey; sortDir: 'asc' | 'desc' }) {
  if (sortBy !== col) return <ArrowUpDown size={12} className="text-ink-faint" />;
  return sortDir === 'asc' ? <ChevronUp size={12} className="text-sage" /> : <ChevronDown size={12} className="text-sage" />;
}

// Componente top-level (non definito in render): sortBy/sortDir/onSort via props.
function SortTh({ col, label, sortBy, sortDir, onSort }: {
  col: SortKey; label: string; sortBy: SortKey; sortDir: 'asc' | 'desc'; onSort: (key: SortKey) => void;
}) {
  return (
    <th className="px-3 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide cursor-pointer hover:text-ink-dark select-none" onClick={() => onSort(col)}>
      <span className="flex items-center gap-1">{label} <SortIcon col={col} sortBy={sortBy} sortDir={sortDir} /></span>
    </th>
  );
}

export default function ConferimentiListaView({ items, total, limit, offset, onPageChange, sortBy, sortDir, onSort, onDelete }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const pages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-stone-mid">
        <table className="w-full text-sm">
          <thead className="bg-stone-bg border-b border-stone-mid">
            <tr>
              <SortTh col="numero" label={t('conferimenti.lista.col_numero')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortTh col="data_prevista" label={t('conferimenti.lista.col_data')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('conferimenti.lista.col_ordine_cliente')}</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('conferimenti.lista.col_peso')}</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('conferimenti.lista.col_peso_reale')}</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('conferimenti.lista.col_area')}</th>
              <SortTh col="status" label={t('conferimenti.lista.col_stato')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-3 text-right text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('conferimenti.lista.col_azioni')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-mid">
            {items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-ink-faint">{t('conferimenti.lista.vuoto')}</td>
              </tr>
            )}
            {items.map((c) => {
              const statusCls = STATUS_CLS[c.status_display] ?? STATUS_CLS[c.status] ?? 'bg-stone-100 text-ink-mid';
              const statusLabel = t(`conferimenti.card.stato.${c.status_display}`, { defaultValue: c.status });
              return (
                <tr key={c.id} className="hover:bg-stone-50 transition-colors cursor-pointer" onClick={() => navigate(`/conferimenti/${c.id}`)}>
                  <td className="px-3 py-3 font-mono text-xs text-ink-dark">{c.numero ?? '—'}</td>
                  <td className="px-3 py-3 text-ink-mid text-xs whitespace-nowrap">
                    {formatDateIT(c.data_prevista)}
                    {c.status_display === 'in_ritardo' && (
                      <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-red-500" title={t('conferimenti.card.stato.in_ritardo')} />
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-xs font-medium text-ink-dark">{c.codice_ordine}</div>
                    <div className="text-xs text-ink-soft truncate max-w-[160px]">{c.customer_ragione_sociale}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-ink-mid whitespace-nowrap">{c.peso_stimato_kg ? `${fmtKg(c.peso_stimato_kg)} kg` : '—'}</td>
                  <td className="px-3 py-3 text-xs text-ink-mid whitespace-nowrap">{c.peso_netto_kg != null ? `${fmtKg(c.peso_netto_kg)} kg` : '—'}</td>
                  <td className="px-3 py-3 text-xs text-ink-mid">{c.area_tentativa_codice ?? '—'}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${statusCls}`}>
                      {statusLabel}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        title={t('conferimenti.lista.dettaglio')}
                        onClick={() => navigate(`/conferimenti/${c.id}`)}
                        className="p-1.5 text-ink-soft hover:text-sage rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      {c.status === 'programmato' && onDelete && (
                        <button
                          type="button"
                          title={t('conferimenti.lista.elimina')}
                          onClick={() => onDelete(c.id)}
                          className="p-1.5 text-ink-soft hover:text-accent-danger rounded transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-ink-soft">
          <span>{t('conferimenti.lista.totali', { total })}</span>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => onPageChange(offset - limit)}
              className="px-2 py-1 border border-stone-border rounded hover:bg-stone-bg disabled:opacity-40"
            >
              {t('conferimenti.lista.prec')}
            </button>
            <span className="px-3 py-1">
              {currentPage} / {pages}
            </span>
            <button
              type="button"
              disabled={currentPage === pages}
              onClick={() => onPageChange(offset + limit)}
              className="px-2 py-1 border border-stone-border rounded hover:bg-stone-bg disabled:opacity-40"
            >
              {t('conferimenti.lista.succ')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
